import Text from 'components/Text/Text';
import { ArchivedLabel } from 'components/Select/ArchivedLabel';
import React, { useState, useEffect } from 'react';
import { IItemForGetShortInfoWithStatus, EntityStatus } from 'types';
import { SHOW_ARCHIVED_RESTAURANTS } from 'appConstants';
import { Checkbox } from 'antd';
import SearchField from 'components/SearchField/SearchField';
import style from './MultipleSelect.module.scss';
import OutsideFormSelect from 'components/Select/OutsideFormSelect';

interface IMultipleSelect {
	data: IItemForGetShortInfoWithStatus[];
	isLoading: boolean;
	refetch: () => void;
	responseError: boolean;
	setIDsRestaurantsToCopyMenu: (value: string[]) => void;
	IDsSelectedRestaurantsToCopyMenu: string[];
}

const MultipleSelect: React.FC<IMultipleSelect> = ({
	data,
	isLoading,
	refetch,
	responseError,
	setIDsRestaurantsToCopyMenu,
	IDsSelectedRestaurantsToCopyMenu,
}) => {
	const [searchString, setSearchString] = useState<string>('');
	const [isSelectedAllRestaurants, setIsSelectedAllRestaurants] = useState<boolean>(false);
	const [showArchived, setShowArchived] = useState<boolean>(
		localStorage.getItem(SHOW_ARCHIVED_RESTAURANTS) === 'true',
	);

	const visibleData = showArchived
		? data
		: data.filter((el) => el.status === EntityStatus.Active || IDsSelectedRestaurantsToCopyMenu.includes(el.id));

	const mappedData = visibleData.map((el) => ({
		...el,
		additionalContent: el.status === EntityStatus.Archived ? <ArchivedLabel /> : undefined,
	}));

	const filteredData = searchString
		? mappedData.filter((el) => el.name.toLowerCase().includes(searchString.toLowerCase()))
		: mappedData;

	const toggleShowArchived = () => {
		localStorage.setItem(SHOW_ARCHIVED_RESTAURANTS, JSON.stringify(!showArchived));
		setShowArchived((prevValue) => !prevValue);
	};

	const handleSelectAllData = () => {
		if (isSelectedAllRestaurants) {
			setIDsRestaurantsToCopyMenu([]);
		} else {
			const IDsOfAllRestaurants = visibleData.map((el) => el.id);
			setIDsRestaurantsToCopyMenu(IDsOfAllRestaurants);
		}
	};

	useEffect(() => {
		if (IDsSelectedRestaurantsToCopyMenu.length === 0) {
			// To turn off checkbox when remove all selected restaurants from list
			setIsSelectedAllRestaurants(false);
		} else if (IDsSelectedRestaurantsToCopyMenu.length === visibleData.length) {
			// To turn on checkbox when select all restaurants in list
			setIsSelectedAllRestaurants(true);
		} else if (IDsSelectedRestaurantsToCopyMenu.length !== visibleData.length) {
			// To turn off checkbox when not all restaurants are selected from the list
			setIsSelectedAllRestaurants(false);
		}
	}, [IDsSelectedRestaurantsToCopyMenu.length, visibleData.length]);

	const renderSelectMenu = (menu: React.ReactElement) => {
		return (
			<>
				<div className={style.selectMenuHeader}>
					<Checkbox
						className={style.checkBox}
						checked={isSelectedAllRestaurants}
						disabled={!visibleData.length}
						onChange={handleSelectAllData}>
						<Text
							translationNameSpace="form"
							translationKey="selectAllRestaurantsLabel"
							asFragment={true}
						/>
					</Checkbox>
					<Checkbox className={style.checkBox} checked={showArchived} onChange={toggleShowArchived}>
						<Text translationNameSpace="modals" translationKey="showArchived" asFragment={true} />
					</Checkbox>
					<SearchField
						onChangeSelectSearchString={setSearchString}
						searchString={searchString}
						className={style.searchField}
					/>
				</div>
				{menu}
			</>
		);
	};

	return (
		<OutsideFormSelect
			testId="MultipleSelect"
			name="IDsSelectedRestaurantsToCopyMenu"
			label="restaurantsLabel"
			value={IDsSelectedRestaurantsToCopyMenu}
			options={filteredData}
			fetchError={responseError}
			loading={isLoading}
			onRefetch={refetch}
			disabled={isLoading}
			onChange={setIDsRestaurantsToCopyMenu}
			counterValue={IDsSelectedRestaurantsToCopyMenu.length}
			withValidation={false}
			dropdownRender={renderSelectMenu}
			mode="multiple"
		/>
	);
};

export default React.memo(MultipleSelect);
